<script lang="ts">
import { Is24Layout, Is24MainNavItem, Is24SideMenuNavItem, MenuItem } from '@smg-real-estate/domus';
import { FavouritesLink as SharedFavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { defineComponent, type Component } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import useConditionalSSRContext from '@/composables/useConditionalSSRContext';
import { getFavouritesAuthToken } from '@/helpers/Auth/getFavouritesAuthToken';
import { InteractionEvent, trackEvent, TrackingData } from '@/helpers/GTM/GTMHelper';
import { getAppMetaInfo } from '@/helpers/Meta/metaInfoHelper';
import { getPage } from '@/helpers/Route/routeHelper';
import { handleNavTracking } from '@/helpers/Tracking/Tracking';
import routingMixin from '@/mixins/routingMixin';
import { setTemporaryUUIDCookie } from '@/store/modules/split/services';
import { defaultSmgSplitSession } from '@/store/modules/split/store';
import { initSplitStoreFromSession } from '@smg-real-estate/vue-experiments';
import { fetchFormData } from './helpers/Ssr/listingSSRHelper';

const FavouritesLink = SharedFavouritesLink as Component;

export default defineComponent({
  name: 'AppIS24',
  components: {
    FavouritesLink,
    Is24Layout,
    Is24MainNavItem,
    Is24SideMenuNavItem,
  },
  mixins: [routingMixin],
  data: function () {
    const ctx = useConditionalSSRContext();
    return {
      smgSplitSession: ctx?.smgSplitSession ?? defaultSmgSplitSession,
      baseUrl: import.meta.env.VUE_APP_BASE_URL,
      isLoggedIn: false,
      favouritesAuthToken: '',
      passThroughHeaders: ctx?.passThroughHeaders ?? {},
    };
  },
  head() {
    return getAppMetaInfo(this.$i18n.locale);
  },
  computed: {
    ...mapGetters({
      splitSessionKey: 'split/getSplitSessionKey',
    }),
    userAccountMenuItem(): MenuItem {
      return {
        label: this.$tm('$is24.nav.harmonize.overviewMenuItem.label'),
        href: `${this.baseUrl}/${this.$i18n.locale}/account-overview`,
      };
    },
    favouritesMenuItem(): MenuItem {
      return this.$tm('$is24.nav.favoriteMenuItem') as unknown as MenuItem;
    },
    favouritesMainNavItemComponent(): typeof Is24MainNavItem {
      return Is24MainNavItem;
    },
    favouritesSideMenuNavItemComponent(): typeof Is24SideMenuNavItem {
      return Is24SideMenuNavItem;
    },
    dropdownMenuItems(): MenuItem {
      if (this.isLoggedIn) {
        return {
          label: this.$tm('$is24.nav.loggedInMenuItem.label'),
          children: this.$tm('$is24.nav.harmonize.privateItems'),
        };
      } else
        return {
          label: this.$tm('$is24.nav.harmonize.overviewMenuItem.label'),
          href: `${this.baseUrl}/${this.$i18n.locale}/account-overview`,
        };
    },
    adMenuItem(): MenuItem {
      return this.$tm('$is24.nav.harmonize.newListingMenuItem') as unknown as MenuItem;
    },
    isFooterHidden(): boolean {
      return true;
    },
    isProduction(): boolean {
      return import.meta.env.VUE_APP_STAGE === 'prod';
    },
  },
  watch: {
    '$route.path': {
      handler(path: string) {
        this.setCurrentPage(getPage(path));
        fetchFormData(this.$store, this.$route, this.passThroughHeaders);
      },
    },
  },
  async mounted() {
    this.updateSplitStoreFromApi();
    window.addEventListener('optanonHandler', () => {
      setTemporaryUUIDCookie(this.splitSessionKey, document.cookie, window.location.href);
      this.updateSplitStoreFromApi();
    });
    this.updateUserDetails();
    this.favouritesAuthToken = await getFavouritesAuthToken(this.$auth0);
  },
  methods: {
    ...mapActions({
      updateSplitStoreFromApi: 'split/updateSplitStoreFromApi',
    }),
    ...mapMutations({
      setCurrentPage: 'common/setCurrentPage',
      setLoggedInUser: 'common/setLoggedInUser',
    }),
    handleTrackFooterClick(data: TrackingData) {
      trackEvent(InteractionEvent.FOOTER_CLICK, {
        h_event_category_006: 'footer_interaction',
        h_event_action_007: data.eventAction,
        h_event_label_008: data.eventLabel,
        non_interaction: false,
      });
    },
    updateUserDetails() {
      if (this.$auth0?.isAuthenticated.value) {
        this.isLoggedIn = true;
        this.setLoggedInUser(this.$auth0?.user.value['cognito:username']);
      } else {
        this.isLoggedIn = false;
      }
    },
    handleMainNavTracking(menuItem: MenuItem, event: Event) {
      handleNavTracking(menuItem, event, false, this.$i18n.locale);
    },
    handleMobileMenuNavTracking(menuItem: MenuItem, event: Event) {
      handleNavTracking(menuItem, event, true, this.$i18n.locale);
    },
  },
  async serverPrefetch(): Promise<void> {
    this.setCurrentPage(getPage(this.$route.path));
    initSplitStoreFromSession(this.$store, this.smgSplitSession);
    await fetchFormData(this.$store, this.$route, this.passThroughHeaders);
  },
});
</script>

<template>
  <Is24Layout
    id="app"
    :baseUrl="baseUrl"
    :currentLanguage="$i18n.locale"
    :currentRoute="$route"
    :getLanguageToLink="getLanguageSwitchLink"
    :hideFooter="isFooterHidden"
    :hideMainNavigation="false"
    :isWhiteLabelSolution="false"
    @trackFooterClick="handleTrackFooterClick"
  >
    <template #headerMetaAction>
      <Is24MainNavItem
        :menuItem="adMenuItem"
        :currentRoute="$route"
        :baseUrl="baseUrl"
        :bordered="true"
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        platform="is24"
        :menuNavItem="favouritesMainNavItemComponent"
        :isProduction="isProduction"
        :baseUrl="baseUrl"
        :authToken="favouritesAuthToken"
        :route="$route"
        @mainNavItemClick="handleMainNavTracking"
      />
      <Is24MainNavItem
        :menuItem="dropdownMenuItems"
        icon="PersonOutline"
        :baseUrl="baseUrl"
        :currentRoute="$route"
        @mainNavItemClick="handleMainNavTracking"
        @dropDownMenuItemClick="handleMainNavTracking"
      />
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        platform="is24"
        :menuNavItem="favouritesSideMenuNavItemComponent"
        :isProduction="isProduction"
        :baseUrl="baseUrl"
        :authToken="favouritesAuthToken"
        :route="$route"
        @sideMenuItemClick="handleMobileMenuNavTracking"
      />
      <Is24SideMenuNavItem
        :menuItem="userAccountMenuItem"
        icon="PersonOutline"
        :baseUrl="baseUrl"
        :currentRoute="$route"
        @sideMenuItemClick="handleMobileMenuNavTracking"
      />
    </template>
    <router-view />
  </Is24Layout>
</template>

<style lang="scss">
@use './assets/globals-is24' as *;
</style>
